import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  HighlightParams,
  HighlightTarget,
  isGroupSubdued,
  isHighlightActive,
  parseHighlightMode,
  shouldOwnScroll,
} from "./highlight-match";

/** Dedupes the scroll across wrappers sharing one deeplink tuple; includes the
 *  router location key so a later deeplink to the same setting scrolls again. */
let lastScrolledTuple: string | null = null;

function tupleKey(locationKey: string, params: HighlightParams): string {
  return [
    locationKey,
    params.mode,
    params.group ?? "",
    params.setting ?? "",
  ].join("|");
}

export interface UseHighlightTargetResult {
  ref: RefObject<HTMLDivElement>;
  /** Param-derived: this wrapper is the deeplink target (should glow). */
  isActive: boolean;
  /** One-shot attention pulse, latched on activation, cleared on animation end. */
  isPulsing: boolean;
  /** A group rendered grey because a setting inside it is also highlighted. */
  isSubdued: boolean;
  onAnimationEnd: () => void;
}

/**
 * Shared behavior for `<GroupHighlight>` / `<SettingHighlight>`: derive
 * active/scroll state from the `?group`/`?setting`/`?highlight` params, own the
 * element ref, scroll once into view on activation, and drive the one-shot pulse
 * latch.
 *
 * The scroll is a legitimate effect — it synchronizes an external system (the
 * browser's scroll position / DOM layout) with the URL deeplink, rather than
 * deriving React state.
 */
export function useHighlightTarget(
  target: HighlightTarget,
  options: {
    disableAutoScroll?: boolean;
    scrollOptions?: ScrollIntoViewOptions;
  } = {},
): UseHighlightTargetResult {
  const { disableAutoScroll = false, scrollOptions } = options;
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params: HighlightParams = {
    group: searchParams.get("group"),
    setting: searchParams.get("setting"),
    mode: parseHighlightMode(searchParams.get("highlight")),
  };

  const isActive = isHighlightActive(target, params);
  const ownsScroll = shouldOwnScroll(target, params);
  const isSubdued = target.kind === "group" && isGroupSubdued(params);
  const key = tupleKey(location.key, params);

  const ref = useRef<HTMLDivElement>(null);
  const [isPulsing, setIsPulsing] = useState(false);
  const pulsedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    // Latch the pulse once per tuple so a later deeplink can replay it.
    if (pulsedKeyRef.current !== key) {
      pulsedKeyRef.current = key;
      setIsPulsing(true);
    }

    if (
      !ownsScroll ||
      disableAutoScroll ||
      lastScrolledTuple === key ||
      typeof window === "undefined" ||
      ref.current === null
    ) {
      return;
    }

    const element = ref.current;
    const prefersReducedMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

    // One frame lets surrounding layout settle so `block: "center"` lands on the
    // element's final position. Mark the tuple as scrolled only once the scroll
    // actually runs, so a remount-before-paint (or a second owner sharing the
    // tuple) still resolves correctly.
    const frame = window.requestAnimationFrame(() => {
      if (lastScrolledTuple === key) {
        return;
      }
      lastScrolledTuple = key;
      element.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "center",
        ...scrollOptions,
      });
    });

    return () => window.cancelAnimationFrame(frame);
    // scrollOptions is per-render config, intentionally excluded from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, ownsScroll, key, disableAutoScroll]);

  const onAnimationEnd = useCallback(() => setIsPulsing(false), []);

  return { ref, isActive, isPulsing, isSubdued, onAnimationEnd };
}
